import { useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Centralized redirect handler for email links and authentication flows
 *
 * Handles two types of redirects:
 * 1. JWT-based redirects (claimToken + redirectTo) - with email validation
 * 2. Plain redirects (redirectTo only) - no validation needed
 *
 * Features:
 * - Stores redirect in sessionStorage with optional expected email
 * - Validates user email against expected email (prevents wrong account usage)
 * - Silent fail for wrong account (no redirect, no toast)
 * - Works with social logins (Google, Discord, Twitch)
 * - Does NOT interfere with existing invite system (joinTeamId, etc.)
 */

const PENDING_REDIRECT_KEY = 'pendingRedirect';
const PENDING_REDIRECT_EMAIL_KEY = 'pendingRedirectEmail';

export const useRedirectHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { t: tCommon } = useTranslation('common');

  /**
   * Store redirect destination in sessionStorage
   * @param redirectPath - The path to redirect to after authentication
   * @param expectedEmail - Optional email that must match the logged-in user for redirect to execute
   */
  const storeRedirect = useCallback(
    (redirectPath: string, expectedEmail?: string) => {
      sessionStorage.setItem(PENDING_REDIRECT_KEY, redirectPath);
      if (expectedEmail) {
        sessionStorage.setItem(
          PENDING_REDIRECT_EMAIL_KEY,
          expectedEmail.toLowerCase(),
        );
      }
    },
    [],
  );

  /**
   * Get stored redirect from sessionStorage
   */
  const getStoredRedirect = useCallback((): string | null => {
    return sessionStorage.getItem(PENDING_REDIRECT_KEY);
  }, []);

  /**
   * Clear stored redirect from sessionStorage
   */
  const clearStoredRedirect = useCallback(() => {
    sessionStorage.removeItem(PENDING_REDIRECT_KEY);
    sessionStorage.removeItem(PENDING_REDIRECT_EMAIL_KEY);
  }, []);

  /**
   * Execute redirect if one is stored
   * Called after successful authentication
   * @param currentUserEmail - The email of the currently logged-in user
   * @returns true if redirect was executed, false otherwise
   */
  const executeStoredRedirect = useCallback(
    (currentUserEmail?: string) => {
      const redirectPath = getStoredRedirect();
      const expectedEmail = sessionStorage.getItem(PENDING_REDIRECT_EMAIL_KEY);

      if (redirectPath) {
        // If expected email exists, validate it matches current user
        if (expectedEmail && currentUserEmail) {
          if (expectedEmail !== currentUserEmail.toLowerCase()) {
            clearStoredRedirect();
            return false;
          }
        }

        // Email matches or no validation needed, proceed with redirect
        clearStoredRedirect();

        // Validate the redirect path (security check)
        try {
          const url = new URL(redirectPath, window.location.origin);
          if (url.origin === window.location.origin) {
            navigate(url.pathname + url.search, { replace: true });
            return true;
          }
        } catch {
          // Invalid URL, don't redirect
          console.warn('Invalid redirect path:', redirectPath);
        }
      }
      return false;
    },
    [getStoredRedirect, clearStoredRedirect, navigate],
  );

  /**
   * Handle "different account" scenario
   */
  const handleDifferentAccount = useCallback(() => {
    toast.error(tCommon('toast.error.differentAccount'));
    clearStoredRedirect();
  }, [clearStoredRedirect, tCommon]);

  /**
   * Check if URL has redirectTo parameter
   */
  const hasRedirectParam = useCallback((): boolean => {
    return searchParams.has('redirectTo');
  }, [searchParams]);

  /**
   * Get redirectTo value from URL
   */
  const getRedirectParam = useCallback((): string | null => {
    return searchParams.get('redirectTo');
  }, [searchParams]);

  /**
   * Handle plain redirectTo (no claimToken)
   * This runs when user arrives with ?redirectTo= but no authentication token
   */
  useEffect(() => {
    const redirectTo = getRedirectParam();

    // Only handle plain redirectTo (without claimToken)
    // claimToken scenarios are handled in App.tsx
    if (redirectTo && !searchParams.has('claimToken')) {
      if (isAuthenticated) {
        clearStoredRedirect();
        navigate(redirectTo, { replace: true });
      } else {
        storeRedirect(redirectTo);
        // The auth modal will be shown by other logic
      }
    }
  }, [
    searchParams,
    getRedirectParam,
    isAuthenticated,
    navigate,
    storeRedirect,
    clearStoredRedirect,
  ]);

  return {
    storeRedirect,
    getStoredRedirect,
    clearStoredRedirect,
    executeStoredRedirect,
    handleDifferentAccount,
    hasRedirectParam,
    getRedirectParam,
  };
};
